// src/AdminAuthors.tsx
// Full, unfiltered author directory — every user, most recently created
// first, with per-row trust-state actions (see AdminAuthorRow.tsx).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { listAdminAuthors, type AdminAuthor } from './adminApi';
import { AdminAuthorRow } from './AdminAuthorRow';
import { AdminNav } from './AdminNav';
import './Admin.css';

export function AdminAuthors() {
  const { author, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [authors, setAuthors] = useState<AdminAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!author || !author.isAdmin)) navigate('/dashboard');
  }, [authLoading, author, navigate]);

  useEffect(() => {
    if (!author?.isAdmin) return;
    listAdminAuthors()
      .then(setAuthors)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load authors.'))
      .finally(() => setLoading(false));
  }, [author]);

  if (authLoading || !author?.isAdmin) return null;

  return (
    <div className="admin-page">
      <AdminNav active="authors" />
      <p className="admin-sub">Every user, most recently created first.</p>

      {error && <p className="page-error-text">{error}</p>}

      {loading ? (
        <p className="admin-sub">Loading…</p>
      ) : (
        <section className="admin-section">
          <h2>Users <span className="admin-count">{authors.length}</span></h2>
          <ul className="admin-list">
            {authors.map(a => (
              <AdminAuthorRow
                key={a.userId}
                author={a}
                onUpdated={updated => setAuthors(prev => prev.map(x => (x.userId === updated.userId ? updated : x)))}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
